import { useCallback, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { MyContext } from "../../context/MyContext";
import AirHeader from "../../components/AirHeader";
import useMyActivityView from "../myActivityV2/hooks/useMyActivityView";
import Warning from "../../components/Warning";
import { Back, Chevron, InfoInfo } from "../../assets/svgs/icons";
import { LogoDefault } from "../../assets/svgs/logos";
import { SubscriptionStyled } from "./styled";
import IcoChat from "../../assets/icons/IcoChat";
import IcoSmile from "../../assets/icons/IcoSmile";
import IcoDocument from "../../assets/icons/IcoDocument";
import { Checkbox } from "@mui/material";
import {
  BillingProduct,
  SubscriptionApi,
  SubscriptionUtils,
  ActiveSubscription,
  ActiveSubscriptionsResponse,
  formatDate,
} from "./utils";

interface SubscriptionStatus {
  productId: string;
  purchaseToken: string;
  orderId: string;
  purchaseState: number;
  isAutoRenewing: boolean;
  isAcknowledged: boolean;
}

interface BillingResult {
  ok: boolean;
  purchaseToken?: string;
  orderId?: string;
  productId?: string;
  purchaseState?: number;
  userId?: string;
  code?: string;
  message?: string;
}

type NavigatorWithFileShare = Navigator & {
  canShare?: (data: { files?: File[] }) => boolean;
  share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
};

const qnaList = [
  {
    question: "구글 플레이 구독 상품 변경 시 결제와 적용은 언제 되나요?",
    answer: (
      <p>
        <strong>- 적용 시점:</strong> 현재 이용 중인 구독 상품의 남은 기간까지는
        기존 서비스가 유지됩니다. <br /> <strong>- 결제 및 전환:</strong>{" "}
        변경하신 새 상품의 요금 결제와 상품 전환은 현재 구독 주기가 종료되는
        다음 갱신일에 자동으로 이루어집니다. 결제 예약 상태는 구글 플레이
        스토어의 ‘정기 결제’ 메뉴에서 확인하실 수 있습니다.
      </p>
    ),
  },
  {
    question: "구글 플레이 정기 결제 해지는 어떻게 하나요?",
    answer: (
      <p>
        <strong>- 해지 방법:</strong> 구글 플레이 스토어의 정기 결제 관리 페이지
        에서 언제든지 해지 가능합니다.{" "}
        {
          "(앱 내 마이페이지 > 이용내역 > [구독 관리]를 통해서도 이동하실 수 있습니다.)"
        }
        <br />
        <strong>- 주의 사항:</strong> 앱 삭제나 회원 탈퇴만으로는 정기 결제가
        취소되지 않으므로 반드시 직접 구독을 해지해 주셔야 합니다. 해지 후에도
        남은 기간 동안은 서비스를 계속 이용하실 수 있으며, 다음 결제일부터
        요금이 청구되지 않습니다.
      </p>
    ),
  },
  {
    question: "환불 정책은 어떻게 되나요?",
    answer: (
      <p>
        <strong>- 환불 원칙:</strong> 정기 결제 상품의 환불은 구글 플레이의 환불
        정책 및 내부 검토 기준에 따라 결정됩니다. <br />{" "}
        <strong>- 환불 신청:</strong>{" "}
        <a
          href="https://support.google.com/googleplay/answer/2479637"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#6d2ef2] font-medium underline hover:opacity-70 transition-opacity"
        >
          구글 플레이 환불 도움말
        </a>
        을 통해 환불 가능 여부를 확인하고 신청하실 수 있습니다. <br />
        <strong>- 기타 문의:</strong> 구글 플레이를 통한 환불이 어렵거나 시스템
        이용에 문제가 있는 경우, 고객센터(dx@dongascience.com)로 문의해 주시기
        바랍니다.
      </p>
    ),
  },
  {
    question: `"결제 수단 문제"로 이용이 제한되었다고 나옵니다. 어떻게 하나요? (보류 상태)`,
    answer: (
      <p>
        - 정기 결제 갱신 시점에 등록된 결제 수단(카드 등)의 한도 초과, 유효기간
        만료 등으로 결제가 실패하면 서비스 이용이 일시 제한(계정 보류)됩니다.
        <br />
        <strong>- 해결 방법:</strong> 구글 플레이{" "}
        <a
          href="https://play.google.com/store/paymentmethods"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#6d2ef2] font-medium underline hover:opacity-70 transition-opacity"
        >
          결제 수단 수정 페이지
        </a>
        에서 유효한 결제 수단으로 업데이트하시면 즉시 다시 이용하실 수 있습니다.
      </p>
    ),
  },
  {
    question: `구독 상품을 해지했는데 왜 아직 이용 가능한가요?`,
    answer: (
      <p>
        - 정기 결제는 '미래의 자동 결제'를 취소하는 개념입니다. 따라서 해지 즉시
        서비스가 종료되는 것이 아니라, 이미 결제가 완료된{" "}
        <strong>현재 이용 주기의 마지막 날까지</strong>는 모든 유료 혜택을
        정상적으로 누리실 수 있습니다.
      </p>
    ),
  },
];

// Utility Functions
const isInApp = () => typeof (window as any).Android !== "undefined";

const cloneJsonValue = <T,>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const getBillingProductsDebugFileName = () => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `air-billing-products-${timestamp}.json`;
};

const triggerJsonFileDownload = (jsonText: string, fileName: string) => {
  const blob = new Blob([jsonText], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
};

// 구독 관리 페이지
const Subscription = () => {
  const { isMobile, user } = useContext(MyContext);
  const { handleGoBack } = useMyActivityView();
  const navigate = useNavigate();

  // States
  const [products, setProducts] = useState<BillingProduct[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionStatus[]>([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState<
    ActiveSubscription[]
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<BillingProduct | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState<
    number | null
  >(null);
  const [billingProductsDebugJsonText, setBillingProductsDebugJsonText] =
    useState<string | null>(null);
  const [billingProductsDebugFileName, setBillingProductsDebugFileName] =
    useState("");
  const [isWarning, setIsWarning] = useState(false);
  const [warnSetting, setWarnSetting] = useState({
    warningTxt: "",
    onClose: () => clearWarnState(),
  });
  const agreementSectionRef = useRef<HTMLDivElement>(null);
  const billingProductsRawRef = useRef<unknown>(null);
  const billingProductsDebugTapCountRef = useRef(0);
  const billingProductsDebugTapTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);

  // 경고 상태 초기화
  const clearWarnState = () => {
    setWarnSetting({
      ...warnSetting,
      warningTxt: "",
    });
    setIsWarning(false);
  };

  // 결제 검증
  const verifyPurchase = useCallback(
    async (data: BillingResult) => {
      // purchasingProduct (최근에 launchBilling 한 상품) 사용

      try {
        const response = await SubscriptionApi.post(
          "/billing/verify-purchase",
          {
            purchaseToken: data.purchaseToken,
            productId: data.productId,
            orderId: data.orderId,
            userId: user?.id || data.userId,
          },
        );

        // 응답 상태 코드 확인
        if (!response.ok) {
          const errorData = await response.text();
          console.error("❌ API 오류 응답:", response.status, errorData);
          console.warn("⚠️ API 검증 실패했지만 구독 완료 페이지로 이동합니다");
        } else {
          const result = await response.json();
          if (!result.ok) {
            console.error("💥 결제 검증 실패:", result.message);
          }
        }

        // API 검증 결과와 관계없이 구독 완료 페이지로 이동
        const navigateState = {
          orderId: data.orderId,
        };
        navigate("/subscription-complete", {
          state: navigateState,
        });
      } catch (err) {
        // console.error("💥 결제 검증 오류:", err);
        // const errorMessage =
        //   err instanceof Error ? err.message : "알 수 없는 오류";
        // console.warn(
        //   `⚠️ 결제 검증 중 오류 발생: ${errorMessage}. 구독 완료 페이지로 이동합니다`,
        // );

        // 에러 발생해도 구독 완료 페이지로 이동
        const navigateState = {
          orderId: data.orderId,
        };
        navigate("/subscription-complete", {
          state: navigateState,
        });
      }
    },
    [navigate, user?.id],
  );

  // QnA 토글 핸들러
  const handleQuestionToggle = (index: number) => {
    if (expandedQuestionIndex === index) {
      // 같은 항목을 누르면 닫기
      setExpandedQuestionIndex(null);
    } else {
      // 다른 항목을 누르면 기존 것 닫고 새로운 것 펼치기
      setExpandedQuestionIndex(index);
    }
  };

  const downloadBillingProductsRawJson = useCallback(async () => {
    const jsonText = JSON.stringify(
      billingProductsRawRef.current ?? null,
      null,
      2,
    );
    const fileName = getBillingProductsDebugFileName();
    const file = new File([jsonText], fileName, {
      type: "application/json;charset=utf-8",
    });
    const navigatorWithShare = navigator as NavigatorWithFileShare;

    if (
      navigatorWithShare.share &&
      navigatorWithShare.canShare?.({ files: [file] })
    ) {
      try {
        await navigatorWithShare.share({
          files: [file],
          title: fileName,
        });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
      }
    }

    triggerJsonFileDownload(jsonText, fileName);
    setBillingProductsDebugFileName(fileName);
    setBillingProductsDebugJsonText(jsonText);
  }, []);

  const handleCopyBillingProductsDebugJson = useCallback(() => {
    if (!billingProductsDebugJsonText) return;
    void navigator.clipboard?.writeText(billingProductsDebugJsonText);
  }, [billingProductsDebugJsonText]);

  const handleBillingProductsDebugTap = useCallback(() => {
    if (!isInApp()) return;

    billingProductsDebugTapCountRef.current += 1;

    if (billingProductsDebugTapTimerRef.current) {
      clearTimeout(billingProductsDebugTapTimerRef.current);
    }

    billingProductsDebugTapTimerRef.current = setTimeout(() => {
      billingProductsDebugTapCountRef.current = 0;
      billingProductsDebugTapTimerRef.current = null;
    }, 2000);

    if (billingProductsDebugTapCountRef.current < 5) return;

    billingProductsDebugTapCountRef.current = 0;
    if (billingProductsDebugTapTimerRef.current) {
      clearTimeout(billingProductsDebugTapTimerRef.current);
      billingProductsDebugTapTimerRef.current = null;
    }

    void downloadBillingProductsRawJson();
  }, [downloadBillingProductsRawJson]);

  // Setup Android Callbacks
  useEffect(() => {
    if (!isInApp()) return;

    // 상품 목록 조회 콜백
    (window as any).onBillingProducts = (data: any) => {
      billingProductsRawRef.current = cloneJsonValue(data);

      if (data.ok) {
        const newData: BillingProduct[] = [];
        data?.products?.forEach((item: BillingProduct) => {
          const parsedItem = SubscriptionUtils.parseProductData({ ...item });
          newData.push(parsedItem);
        });
        setProducts(newData);
        setError(null);
      } else {
        setError(data.message || "상품 조회 실패");
        console.error("상품 조회 실패:", data.message);
      }
      setLoading(false);
    };

    // 결제 결과 콜백
    (window as any).onBillingResult = (data: BillingResult) => {
      setIsProcessing(false);

      if (data.ok) {
        // 결제 성공 - 서버 검증
        verifyPurchase(data);
      } else if (data.code === "USER_CANCELED") {
        setError("결제가 취소되었습니다");
      } else {
        setError(data.message || "결제 실패");
        console.error("결제 실패:", data.message);
      }
    };

    // 구독 상태 조회 콜백
    (window as any).onSubscriptionStatus = (data: any) => {
      if (data.ok) {
        setSubscriptions(data.subscriptions || []);
        setError(null);
      } else {
        console.error("구독 상태 조회 실패:", data.message);
      }
    };

    return () => {
      delete (window as any).onBillingProducts;
      delete (window as any).onBillingResult;
      delete (window as any).onSubscriptionStatus;
    };
  }, [verifyPurchase]);

  useEffect(() => {
    return () => {
      if (billingProductsDebugTapTimerRef.current) {
        clearTimeout(billingProductsDebugTapTimerRef.current);
      }
    };
  }, []);

  // 상품 목록 조회
  const queryBillingProducts = () => {
    if (!isInApp()) return;
    setLoading(true);
    const android = (window as any).Android;
    if (android?.queryBillingProducts) {
      android.queryBillingProducts();
    }
  };

  // 구독 상태 조회
  const querySubscriptionStatus = () => {
    if (!isInApp()) return;
    const android = (window as any).Android;
    if (android?.querySubscriptionStatus) {
      android.querySubscriptionStatus();
    }
  };

  // 경고 메시지 표시 후 스크롤 이동
  const showWarningAndScroll = (message: string) => {
    setWarnSetting({
      ...warnSetting,
      warningTxt: message,
    });
    setIsWarning(true);

    // 구매 유의사항 섹션으로 스크롤 이동
    if (agreementSectionRef.current) {
      setTimeout(() => {
        agreementSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // 결제 실행
  const handleLaunchBilling = () => {
    if (!selectedProduct || !isInApp()) {
      alert("앱에서만 결제가 가능합니다");
      return;
    }

    // 이용중인 상품인지 확인
    const isCurrentlySubscribed = activeSubscriptions.some(
      (s) => s.productId === selectedProduct.productId,
    );
    if (isCurrentlySubscribed) {
      alert("이미 이용중인 상품입니다");
      return;
    }

    if (!isAgreed) {
      showWarningAndScroll("구매 유의사항을 확인해주세요.");
      return;
    }

    setIsProcessing(true);
    const android = (window as any).Android;
    if (android?.launchBilling) {
      android.launchBilling(
        selectedProduct.productId,
        selectedProduct.offerToken,
        user?.id || "",
      );
    }
  };
  const renderSubscriptionItem = (sub: ActiveSubscription) => {
    if (sub.statusCode === "payment_failed") {
      return (
        <div key={sub.productId} className="product-card black">
          <div className="badge red">결제 실패</div>
          <div className="product-title">{sub.productName}</div>
          <div className="kv-item">
            <div>결제 유효 기간</div>
            <div>{formatDate(sub.gracePeriodEndDate)}</div>
          </div>
          <div className="error-msg">
            <span>
              <InfoInfo style={{ fill: "#f40" }} />
            </span>
            <span>구독 관리에서 결제 수단을 확인하세요.</span>
          </div>
          <button
            className="manage-btn"
            onClick={handleOpenSubscriptionManagement}
          >
            구독 관리
          </button>
        </div>
      );
    }
    return (
      <div key={sub.productId} className="product-card">
        <div className="badge">이용중</div>
        <div className="product-title">{sub.productName}</div>
        <div className="kv-item">
          <div>남은 기간</div>
          <div>
            {sub.remainingDays}일
            {sub.nextBillingDate && ` (${formatDate(sub.nextBillingDate)}까지)`}
          </div>
        </div>
        {sub.nextBillingDate && (
          <>
            <div className="kv-item">
              <div>다음 결제일</div>
              <div>{formatDate(sub.nextBillingDate)}</div>
            </div>
            <div className="payment-area">
              <div>결제 예정 금액</div>
              <div className="amt">{sub.expectedAmount.toLocaleString()}원</div>
            </div>
          </>
        )}
      </div>
    );
  };

  // 구독 관리 열기
  const handleOpenSubscriptionManagement = () => {
    if (!isInApp()) return;
    const android = (window as any).Android;
    if (android?.openSubscriptionManagement) {
      android.openSubscriptionManagement();
    }
  };

  // 활성 구독 목록 조회
  const queryActiveSubscriptions = useCallback(async () => {
    if (!user?.id) {
      setActiveSubscriptions([]);
      return;
    }

    try {
      const response = await SubscriptionApi.get(
        `/billing/active-subscriptions?userId=${encodeURIComponent(user.id)}`,
      );

      if (!response.ok) {
        console.error("활성 구독 조회 실패:", response.status);
        setActiveSubscriptions([]);
      } else {
        const result = (await response.json()) as ActiveSubscriptionsResponse;
        setActiveSubscriptions(
          Array.isArray(result.subscriptions) ? result.subscriptions : [],
        );
      }
    } catch (err) {
      console.error("활성 구독 조회 중 오류:", err);
      setActiveSubscriptions([]);
    }
  }, [user?.id]);

  // 초기 데이터 로드
  useEffect(() => {
    if (isInApp()) {
      queryBillingProducts();
      querySubscriptionStatus();
    }
    // 활성 구독 목록은 앱 환경과 무관하게 항상 조회
    queryActiveSubscriptions();
  }, [queryActiveSubscriptions]);

  // 이용중인 상품이 있으면 자동으로 선택
  useEffect(() => {
    if (subscriptions.length > 0 && products.length > 0) {
      const activeSubscription = subscriptions.find((s) => s.isAutoRenewing);
      if (activeSubscription) {
        const activeProduct = products.find(
          (p) => p.productId === activeSubscription.productId,
        );
        if (activeProduct) {
          setSelectedProduct(activeProduct);
        }
      }
    }
  }, [subscriptions, products]);

  // selectedProduct를 sessionStorage에 저장/제거 (Sparrow #8953221, #8953222)
  useEffect(() => {
    if (selectedProduct) {
      sessionStorage.setItem("selectedProduct", JSON.stringify(selectedProduct));
    } else {
      sessionStorage.removeItem("selectedProduct");
    }
  }, [selectedProduct]);

  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
        {isMobile ? (
          <AirHeader type="sub" title="구독 상품" />
        ) : (
          <header className="relative flex items-center justify-center bg-white w-full h-[64px] shrink-0">
            {/* Back Button */}
            <button
              onClick={handleGoBack}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-4"
              aria-label="뒤로가기"
            >
              <Back className="w-6 h-6 text-[#212121]" />
            </button>

            {/* Logo */}
            <LogoDefault className="w-[100px] h-[25px]" />
          </header>
        )}

        <div className="flex flex-col min-h-0 overflow-hidden flex-1">
          <div className="content overflow-y-auto min-h-0">
            {error && (
              <div className="px-6 py-4 bg-red-50 border border-red-200 text-red-700">
                {error}
              </div>
            )}

            <SubscriptionStyled.HeroSection>
              <div className="title-area">
                <h3>탐구활동 AI 솔루션</h3>
                <h2>
                  튜터 소크라테스에서, <br />
                  모든 학습을 시작하세요
                </h2>
              </div>
              <div className="desc">
                궁금한 건 바로 질문! 실전 탐구 맞춤형 답변 <br />
                입시 면접 완벽 대비, 질문으로 사고를 확장하는 <br />
                소크라테스식 1:1 AI 모의 면접
              </div>
              <div className="icons">
                {[
                  { name: "전문 질문답변", icon: <IcoChat /> },
                  { name: "AI 튜터", icon: <IcoSmile /> },
                  { name: "AI 모의면접", icon: <IcoDocument /> },
                ].map((item, index) => (
                  <div className="icon-item" key={index}>
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
              <img
                src="https://img.dongascience.com/air/air_ticket_char.png"
                alt="Air Ticket Character"
              />
            </SubscriptionStyled.HeroSection>

            {activeSubscriptions.length > 0 && (
              <SubscriptionStyled.UsedProductSection>
                <h3>현재 이용 중인 구독 상품</h3>
                <div className="product-list">
                  {activeSubscriptions.map((sub) =>
                    renderSubscriptionItem(sub),
                  )}
                </div>
              </SubscriptionStyled.UsedProductSection>
            )}

            <SubscriptionStyled.AllProductSection>
              <h3
                onClick={handleBillingProductsDebugTap}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleBillingProductsDebugTap();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                모든 구독 상품 테스트
              </h3>
              <div className="product-list">
                {loading ? (
                  <div className="py-8 text-center text-gray-500">
                    상품 정보를 불러오는 중입니다...
                  </div>
                ) : products.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    {isInApp()
                      ? "상품을 불러올 수 없습니다"
                      : "앱에서만 결제가 가능합니다"}
                  </div>
                ) : (
                  products.map((product) => {
                    const isActive = activeSubscriptions.some(
                      (s) => s.productId === product.productId,
                    );
                    const isSelected =
                      selectedProduct?.productId === product.productId;

                    return (
                      <div
                        key={product.productId}
                        className={`product-card ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => setSelectedProduct(product)}
                        role="button"
                        tabIndex={0}
                      >
                        {isActive && <div className="badge">현재 이용중</div>}
                        <div className="product-title">{product.title}</div>
                        <div className="price">{product.price}</div>
                        {product.trialPeriod && (
                          <div className="free-area">
                            <div className="left">
                              <div>무료체험 혜택</div>
                              <div>{product.trialPeriodDays}</div>
                            </div>
                            <div className="right">
                              <div className="badge outlined">증정</div>
                            </div>
                          </div>
                        )}
                        {product.billingPeriod && isSelected && (
                          <div className="sub-desc-area">
                            <div>정식 구독 기간</div>
                            <div>{product.billingPeriodDays}</div>
                          </div>
                        )}
                        {product.trialPeriod && (
                          <div className="desc">
                            ※ 무료체험 종료 후 자동으로 결제되며 구독이
                            시작됩니다
                          </div>
                        )}
                        {isActive && (
                          <div className="desc">※ 현재 이용중 상품입니다.</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </SubscriptionStyled.AllProductSection>
            <SubscriptionStyled.QnaSection>
              <h3>자주 묻는 질문</h3>
              <div className="question-list">
                {qnaList.map((item, index) => (
                  <div
                    key={index}
                    className={`question-item ${
                      expandedQuestionIndex === index ? "is-selected" : ""
                    }`}
                  >
                    <div
                      className="question"
                      onClick={() => handleQuestionToggle(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleQuestionToggle(index);
                        }
                      }}
                    >
                      <span>{item.question}</span>
                      <span>
                        <Chevron />
                      </span>
                    </div>
                    <div className="answer">{item.answer}</div>
                  </div>
                ))}
              </div>
            </SubscriptionStyled.QnaSection>
            <SubscriptionStyled.AgreementSection ref={agreementSectionRef}>
              <div className="desc">
                구독은 Google Play 계정으로 청구되며 설정에서 언제든 취소
                가능합니다
              </div>
              <div className="checkbox-area">
                <Checkbox
                  className="checkbox"
                  checked={isAgreed}
                  onChange={(e) => setIsAgreed(e.target.checked)}
                />
                <span className="label">
                  구매 유의사항 및 결제 내용을 확인하였습니다.
                </span>
              </div>
            </SubscriptionStyled.AgreementSection>
          </div>
        </div>

        {/* Footer - 고정 */}
        {/* Footer - 고정 */}
        <SubscriptionStyled.Footer>
          <div className="selected-item">
            <span className="item-name">
              {selectedProduct?.title || "상품을 선택해주세요"}
            </span>
            <span className="item-price">{selectedProduct?.price || ""}</span>
          </div>
          <button
            className={`purchase-btn ${isAgreed ? "" : "disabled"}`}
            onClick={handleLaunchBilling}
            disabled={
              !selectedProduct ||
              !isInApp() ||
              isProcessing ||
              activeSubscriptions.some(
                (s) => s.productId === selectedProduct?.productId,
              )
            }
          >
            {activeSubscriptions.some(
              (s) => s.productId === selectedProduct?.productId,
            )
              ? "이용중"
              : isProcessing
              ? "처리 중..."
              : activeSubscriptions.length > 0
              ? "이용권 변경하기"
              : "구독하기"}
          </button>
          {!isInApp() && (
            <div className="text-center text-gray-500 text-sm mt-2">
              앱에서만 결제가 가능합니다
            </div>
          )}
        </SubscriptionStyled.Footer>
      </div>
      {billingProductsDebugJsonText !== null && (
        <div className="fixed inset-x-4 bottom-4 z-[9999] max-h-[60vh] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900">
                Billing Products JSON
              </div>
              <div className="truncate text-xs text-gray-500">
                {billingProductsDebugFileName}
              </div>
            </div>
            <button
              className="shrink-0 rounded bg-gray-100 px-3 py-1 text-sm text-gray-700"
              onClick={() => setBillingProductsDebugJsonText(null)}
            >
              닫기
            </button>
          </div>
          <textarea
            className="h-[38vh] w-full resize-none bg-gray-50 p-3 font-mono text-xs text-gray-800 outline-none"
            readOnly
            value={billingProductsDebugJsonText}
          />
          <div className="flex justify-end gap-2 border-t border-gray-100 p-3">
            <button
              className="rounded bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700"
              onClick={handleCopyBillingProductsDebugJson}
            >
              JSON 복사
            </button>
            <button
              className="rounded bg-[#6d2ef2] px-3 py-2 text-sm font-semibold text-white"
              onClick={() =>
                triggerJsonFileDownload(
                  billingProductsDebugJsonText,
                  billingProductsDebugFileName,
                )
              }
            >
              다시 저장
            </button>
          </div>
        </div>
      )}
      {isWarning && <Warning settings={warnSetting} />}
    </div>
  );
};

export default Subscription;
